import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Thermometer, RotateCcw, Package, LayoutDashboard,
  FileCheck, Camera, ClipboardList, AlertTriangle,
  ArrowRight, Check, Timer,
  Layers, Flame, ShieldCheck, Leaf,
  ChevronRight, Target, Droplets,
  ChevronLeft, MapPin,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import DemoForm from "../components/DemoForm";

/* ──────────────────────────────────────────────
   CSS MOCKUP COMPONENTS — accurate replicas
   of the actual app UI, rendered in pure JSX
   ────────────────────────────────────────────── */

/** Browser window frame for desktop mockups */
function BrowserFrame({ children, url = "compostdeveloper.com/app" }: { children: React.ReactNode; url?: string }) {
  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-2xl shadow-forest-900/10 dark:shadow-black/30">
      <div className="flex items-center gap-2 px-3 py-2 bg-secondary/60 border-b border-border">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-400/80" />
          <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
          <div className="w-2 h-2 rounded-full bg-green-400/80" />
        </div>
        <div className="flex-1 mx-2">
          <div className="bg-background/60 rounded-md px-3 py-0.5 text-[9px] text-muted-foreground/70 text-center font-mono">
            {url}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Phone frame for mobile mockups */
function PhoneFrame({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <div className="overflow-hidden rounded-[2.35rem] border-[5px] border-[#1a2b22] bg-[#f7f8f4] shadow-[0_28px_60px_-18px_rgba(26,43,34,0.55)]">
        <div className="flex items-center justify-between px-5 pt-2.5 text-[8px] font-semibold text-[#1a2b22]">
          <span>9:41</span>
          <span className="h-3.5 w-[72px] rounded-full bg-[#1a2b22]" />
          <span>LTE</span>
        </div>
        {children}
        <div className="flex justify-center py-2">
          <div className="h-1 w-24 rounded-full bg-[#1a2b22]/25" />
        </div>
      </div>
    </div>
  );
}

function FieldBrandBar() {
  return (
    <div className="flex items-center justify-between border-b border-[#e0e6de] bg-[#f7f8f4] px-3 py-2">
      <span className="text-[10px] font-semibold tracking-tight text-[#20392d]">
        Compost <span className="text-[#3a8a40]">Developer</span>
      </span>
      <span className="text-[11px] text-[#2e5e32]">☰</span>
    </div>
  );
}

function FieldFooter() {
  return (
    <div className="px-3 pb-2 pt-1">
      <div className="flex h-9 items-center justify-center rounded-xl bg-[#2e5e32] text-[10px] font-bold text-white">
        + Record
      </div>
    </div>
  );
}

/** Mini stat card (used in dashboard mockup) */
function MiniStat({ label, value, icon: Icon, color }: { label: string; value: string; icon: React.ElementType; color: string }) {
  const colorMap: Record<string, string> = {
    forest: "bg-forest-100 dark:bg-forest-900/30 text-forest-600 dark:text-forest-400",
    amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    red: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
  };
  return (
    <div className="bg-card border border-border rounded-lg p-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        <div className={`w-5 h-5 rounded flex items-center justify-center ${colorMap[color]}`}>
          <Icon size={10} />
        </div>
      </div>
      <p className="text-sm font-bold text-foreground leading-none">{value}</p>
    </div>
  );
}

/** Hero dashboard mockup — full desktop manager view */
function DashboardMockup() {
  return (
    <BrowserFrame>
      <div className="flex min-h-[340px]">
        {/* Sidebar */}
        <div className="w-36 bg-secondary/30 border-r border-border p-2.5 hidden md:flex flex-col">
          <div className="flex items-center gap-1.5 mb-5 px-1">
            <div className="w-5 h-5 rounded bg-forest-600 flex items-center justify-center">
              <Leaf size={10} className="text-white" />
            </div>
            <span className="text-[9px] font-bold text-foreground">Compost<span className="text-forest-600">Dev</span></span>
          </div>
          <div className="space-y-0.5">
            {[
              { name: "Dashboard", icon: LayoutDashboard, active: true },
              { name: "Windrows", icon: Layers, active: false },
              { name: "Temperature", icon: Thermometer, active: false },
              { name: "Turns", icon: RotateCcw, active: false },
              { name: "Materials", icon: Package, active: false },
              { name: "Reports", icon: FileCheck, active: false },
            ].map(({ name, icon: NavIcon, active }) => (
              <div key={name} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] ${
                active
                  ? "bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 font-semibold"
                  : "text-muted-foreground"
              }`}>
                <NavIcon size={10} />
                {name}
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 p-3 bg-background overflow-hidden">
          <p className="text-[10px] font-bold text-foreground mb-0.5">Operations Dashboard</p>
          <p className="text-[7px] text-muted-foreground mb-2.5">Real-time facility overview</p>

          {/* Stats grid */}
          <div className="grid grid-cols-4 gap-1.5 mb-2.5">
            <MiniStat label="Active Windrows" value="6" icon={Layers} color="forest" />
            <MiniStat label="Avg Temperature" value="147°F" icon={Flame} color="amber" />
            <MiniStat label="Compliance" value="83%" icon={ShieldCheck} color="blue" />
            <MiniStat label="Temp Alerts" value="1" icon={AlertTriangle} color="red" />
          </div>

          {/* Quick Actions + Alerts split */}
          <div className="grid grid-cols-5 gap-1.5 mb-2.5">
            <div className="col-span-3 bg-card border border-border rounded-lg p-2">
              <p className="text-[8px] font-bold text-foreground mb-1.5">Quick Actions</p>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { label: "Log Temp", color: "amber", icon: Thermometer },
                  { label: "Record Turn", color: "blue", icon: RotateCcw },
                  { label: "Windrows", color: "forest", icon: Layers },
                ].map(({ label, color, icon: BtnIcon }) => (
                  <div key={label} className="flex flex-col items-center gap-1 p-1.5 rounded-lg border border-border bg-background">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                      color === "amber" ? "bg-amber-100 dark:bg-amber-900/30" :
                      color === "blue" ? "bg-blue-100 dark:bg-blue-900/30" :
                      "bg-forest-100 dark:bg-forest-900/30"
                    }`}>
                      <BtnIcon size={10} className={
                        color === "amber" ? "text-amber-600 dark:text-amber-400" :
                        color === "blue" ? "text-blue-600 dark:text-blue-400" :
                        "text-forest-600 dark:text-forest-400"
                      } />
                    </div>
                    <span className="text-[7px] font-semibold text-foreground">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-2 bg-card border border-border rounded-lg p-2">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[8px] font-bold text-foreground">Temp Alerts</p>
                <span className="text-[6px] bg-red-100 dark:bg-red-900/30 text-red-600 px-1 py-0.5 rounded-full font-bold">1 below</span>
              </div>
              <div className="space-y-1">
                {[
                  { name: "Row A-12", temp: "152°F", ok: true },
                  { name: "Row C-03", temp: "128°F", ok: false },
                  { name: "Row B-07", temp: "148°F", ok: true },
                ].map(({ name, temp, ok }) => (
                  <div key={name} className={`flex items-center justify-between px-1.5 py-1 rounded text-[7px] ${
                    !ok ? "bg-red-50 dark:bg-red-950/20" : "bg-background"
                  }`}>
                    <span className="text-foreground font-medium">{name}</span>
                    <span className={`font-bold ${ok ? "text-forest-600 dark:text-forest-400" : "text-red-600 dark:text-red-400"}`}>{temp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-card border border-border rounded-lg p-2">
            <p className="text-[8px] font-bold text-foreground mb-1.5">Recent Activity</p>
            <div className="space-y-1">
              {[
                { type: "temp", who: "Mike R.", detail: "148°F", windrow: "Row A-12" },
                { type: "turn", who: "Carlos M.", detail: "Turn #4", windrow: "Row B-07" },
                { type: "temp", who: "Sarah L.", detail: "155°F", windrow: "Row D-01" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 py-1 border-b border-border/30 last:border-0">
                  <div className={`w-4 h-4 rounded flex items-center justify-center ${
                    item.type === "temp" ? "bg-amber-100 dark:bg-amber-900/20" : "bg-blue-100 dark:bg-blue-900/20"
                  }`}>
                    {item.type === "temp"
                      ? <Thermometer size={8} className="text-amber-600 dark:text-amber-400" />
                      : <RotateCcw size={8} className="text-blue-600 dark:text-blue-400" />
                    }
                  </div>
                  <span className="text-[7px] text-foreground flex-1">
                    <b>{item.who}</b> <span className="text-muted-foreground">logged</span> <b>{item.detail}</b> <span className="text-muted-foreground">on</span> {item.windrow}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

/** Operator rows list — matches the iPhone field app */
function OperatorDashboardMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#f7f8f4]" style={{ minHeight: 430 }}>
        <FieldBrandBar />
        <div className="px-3 pt-2.5">
          <p className="text-[8px] font-medium text-[#617267]">West yard</p>
          <div className="mt-0.5 flex items-end justify-between">
            <h2 className="text-[15px] font-bold text-[#20392d]">Rows</h2>
            <span className="text-[8px] text-[#2e5e32]">Map · Filter</span>
          </div>
          <p className="mb-2 mt-1 text-[7px] text-[#617267]">4 rows · Active</p>
          {[
            { name: "Pad A — 12", id: "WR-A12", temp: "152.0°F", status: "Active" },
            { name: "Pad B — 07", id: "WR-B07", temp: "148.4°F", status: "Active" },
            { name: "Pad C — 03", id: "WR-C03", temp: "128.1°F", status: "Cooking" },
            { name: "Pad D — 01", id: "WR-D01", temp: "155.2°F", status: "Active" },
          ].map((w, i) => (
            <div key={w.id} className={`border-t border-[#e0e6de] py-2 ${i === 0 ? "border-t-0" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className="flex-1 text-[10px] font-semibold text-[#20392d]">{w.name}</span>
                <span className="text-[10px] font-bold tabular-nums text-[#2e5e32]">{w.temp}</span>
                <ChevronRight size={11} className="text-[#617267]/50" />
              </div>
              <div className="mt-0.5 flex items-center justify-between">
                <span className="text-[7px] text-[#617267]">{w.id} · Windrow</span>
                <span className="rounded-full bg-[#eaf1e6] px-1.5 py-0.5 text-[6px] font-semibold text-[#38603a]">{w.status}</span>
              </div>
            </div>
          ))}
        </div>
        <FieldFooter />
      </div>
    </PhoneFrame>
  );
}

/** Temperature Logging mockup — operator phone view */
function TempLoggingMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#f7f8f4]" style={{ minHeight: 430 }}>
        <FieldBrandBar />
        <div className="px-3 py-3">
          <div className="mb-3 flex items-center gap-1 text-[8px] text-[#2e5e32]">
            <ChevronLeft size={10} /> Rows
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Form header */}
            <div className="p-3 border-b border-border bg-gradient-to-r from-amber-50 to-amber-50/50 dark:from-amber-900/10 dark:to-transparent">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Flame size={14} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h2 className="text-[11px] font-bold text-foreground">Log Temperature</h2>
                  <p className="text-[7px] text-muted-foreground">Record a windrow temperature reading</p>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-3">
              {/* Windrow selector */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Select Windrow</label>
                <div className="px-2 py-1.5 bg-background border border-border rounded-lg text-[9px] text-foreground">
                  Row A-12 (WR-A12)
                </div>
              </div>

              {/* Photo area */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Photo of Thermometer</label>
                <div className="bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 rounded-lg px-2 py-1 flex items-center gap-1.5">
                  <span className="text-[8px] text-forest-700 dark:text-forest-400 font-medium">AI detected 145°F — confirm below</span>
                </div>
              </div>

              {/* Temperature big input */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Temperature (°F)</label>
                <div className="relative">
                  <div className="w-full px-3 py-3 text-center text-2xl font-bold border-2 border-forest-300 dark:border-forest-700 bg-forest-50 dark:bg-forest-900/10 text-forest-600 dark:text-forest-400 rounded-2xl">
                    145
                  </div>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground/50">°F</span>
                </div>
                <p className="text-[7px] text-forest-600 dark:text-forest-400 mt-1 font-semibold flex items-center gap-0.5">
                  <Check size={7} /> Above 131°F — meets compliance
                </p>
              </div>

              {/* Probe location */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-0.5 mb-1">
                  <Target size={7} /> Probe Location
                </label>
                <div className="grid grid-cols-5 gap-1">
                  {["center", "north", "south", "east", "west"].map((loc, i) => (
                    <div key={loc} className={`px-1 py-1.5 text-[7px] font-semibold text-center rounded-lg capitalize ${
                      i === 0 ? "bg-forest-600 text-white" : "bg-secondary text-muted-foreground border border-border"
                    }`}>{loc}</div>
                  ))}
                </div>
              </div>

              <div className="w-full py-2.5 bg-forest-600 text-white text-[10px] font-bold rounded-xl text-center">
                Save reading
              </div>
            </div>
          </div>
        </div>
        <FieldFooter />
      </div>
    </PhoneFrame>
  );
}

/** Turn Logging mockup — operator phone view */
function TurnLoggingMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#f7f8f4]" style={{ minHeight: 430 }}>
        <FieldBrandBar />
        <div className="px-3 py-3">
          <div className="mb-3 flex items-center gap-1 text-[8px] text-[#2e5e32]">
            <ChevronLeft size={10} /> Rows
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="p-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-50/50 dark:from-blue-900/10 dark:to-transparent">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <RotateCcw size={14} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-[11px] font-bold text-foreground">Record Turn</h2>
                  <p className="text-[7px] text-muted-foreground">Log a windrow turning event</p>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-3">
              {/* Windrow selector */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Select Windrow</label>
                <div className="px-2 py-1.5 bg-background border border-border rounded-lg text-[9px] text-foreground">
                  Row B-07 (WR-B07)
                </div>
              </div>

              {/* Equipment grid */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Equipment Used</label>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { label: "Windrow Turner", emoji: "🔄", active: true },
                    { label: "Front Loader", emoji: "🚜", active: false },
                    { label: "Backhoe", emoji: "⛏️", active: false },
                    { label: "Excavator", emoji: "🏗️", active: false },
                    { label: "Skid Steer", emoji: "🚧", active: false },
                    { label: "Manual", emoji: "🧑‍🔧", active: false },
                  ].map(({ label, emoji, active }) => (
                    <div key={label} className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl text-center ${
                      active ? "bg-blue-600 text-white" : "bg-secondary border border-border text-muted-foreground"
                    }`}>
                      <span className="text-sm">{emoji}</span>
                      <span className="text-[6px] font-semibold leading-tight">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Water toggle */}
              <div className="flex items-center justify-between p-2 bg-background border border-border rounded-xl">
                <div className="flex items-center gap-1.5">
                  <Droplets size={12} className="text-blue-500" />
                  <span className="text-[9px] font-semibold text-foreground">Water Added?</span>
                </div>
                <div className="w-8 h-4 bg-blue-500 rounded-full flex items-center justify-end px-0.5">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
              </div>

              {/* Submit */}
              <div className="w-full py-2.5 bg-blue-600 text-white text-[10px] font-bold rounded-xl text-center">
                Record Turn #5
              </div>
            </div>
          </div>
        </div>

        <FieldFooter />
      </div>
    </PhoneFrame>
  );
}

/** Material Logging mockup — operator phone view */
function MaterialLoggingMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#f7f8f4]" style={{ minHeight: 430 }}>
        <FieldBrandBar />
        <div className="px-3 py-3">
          <div className="mb-3 flex items-center gap-1 text-[8px] text-[#2e5e32]">
            <ChevronLeft size={10} /> Rows
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="p-3 border-b border-border bg-gradient-to-r from-forest-50 to-forest-50/50 dark:from-forest-900/10 dark:to-transparent">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-forest-100 dark:bg-forest-900/30 rounded-lg flex items-center justify-center">
                  <Package size={14} className="text-forest-600 dark:text-forest-400" />
                </div>
                <div>
                  <h2 className="text-[11px] font-bold text-foreground">Add Material</h2>
                  <p className="text-[7px] text-muted-foreground">Log material addition to windrow</p>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-3">
              {/* Windrow selector */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Select Windrow</label>
                <div className="px-2 py-1.5 bg-background border border-border rounded-lg text-[9px] text-foreground">
                  Row A-12 (WR-A12)
                </div>
              </div>

              {/* Material type grid */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Material Type</label>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { name: "Green Waste", emoji: "🌿", active: true },
                    { name: "Food Waste", emoji: "🍎", active: false },
                    { name: "Wood Chips", emoji: "🪵", active: false },
                    { name: "Biosolids", emoji: "💧", active: false },
                    { name: "Manure", emoji: "🐄", active: false },
                    { name: "Straw/Hay", emoji: "🌾", active: false },
                  ].map(({ name, emoji, active }) => (
                    <div key={name} className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl ${
                      active ? "bg-forest-600 text-white" : "bg-secondary border border-border text-muted-foreground"
                    }`}>
                      <span className="text-sm">{emoji}</span>
                      <span className="text-[6px] font-semibold leading-tight text-center">{name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tonnage */}
              <div>
                <label className="text-[7px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Tonnage</label>
                <div className="w-full px-3 py-2 text-center text-xl font-bold border-2 border-forest-300 dark:border-forest-700 bg-forest-50 dark:bg-forest-900/10 text-forest-600 dark:text-forest-400 rounded-2xl">
                  12.5 <span className="text-xs text-muted-foreground/50">tons</span>
                </div>
              </div>

              {/* Submit */}
              <div className="w-full py-2.5 bg-forest-600 text-white text-[10px] font-bold rounded-xl text-center">
                Add Material
              </div>
            </div>
          </div>
        </div>

        <FieldFooter />
      </div>
    </PhoneFrame>
  );
}

/* ──────────────────────────────────────────────
   MAIN LANDING PAGE
   ────────────────────────────────────────────── */

const SHOWCASE_SCREENS = [
  { id: "dashboard", label: "Rows on the iPhone", component: OperatorDashboardMockup },
  { id: "temp", label: "Photograph the thermometer", component: TempLoggingMockup },
  { id: "turn", label: "Record a turn", component: TurnLoggingMockup },
  { id: "material", label: "Add material", component: MaterialLoggingMockup },
];

export default function LandingPage() {
  const [activeScreen, setActiveScreen] = useState(0);
  const ActiveMockup = SHOWCASE_SCREENS[activeScreen].component;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="landing-grain landing-hero-wash relative overflow-hidden pt-24 pb-16 lg:pt-28 lg:pb-24">
        <div className="relative z-[2] mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-8">
            <div>
              <p className="label-caps text-earth-600">
                iPhone in the yard · desktop in the office
              </p>
              <h1 className="heading-display mt-4 max-w-[14ch] text-[2.75rem] leading-[1.05] text-[#1a2419] sm:text-6xl lg:text-[4.4rem]">
                The yard, on the phone.
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-[#3d4a40]">
                Photograph the thermometer. Save the recipe. See every pile — even when you are not standing next to it.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/book-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2e5e32] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_14px_28px_-12px_rgba(46,94,50,0.7)] transition-colors hover:bg-[#264027]"
                >
                  Book a demo
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-[#20392d] underline-offset-4 hover:underline"
                >
                  Log in
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4a574e]">
                <span className="flex items-center gap-1.5"><Check size={14} className="text-[#2e5e32]" /> Photo temperatures</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-[#2e5e32]" /> Recipes on the pile</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-[#2e5e32]" /> Turns &amp; materials</span>
              </div>
            </div>

            <div className="relative mx-auto h-[420px] w-full max-w-[540px] sm:h-[500px] lg:h-[540px]">
              <div className="absolute right-0 top-4 hidden w-[90%] origin-top-right rotate-[-3deg] opacity-75 [mask-image:linear-gradient(to_bottom,black_50%,transparent)] sm:block">
                <DashboardMockup />
              </div>
              <div className="absolute left-1/2 top-2 w-[240px] -translate-x-1/2 sm:left-0 sm:top-auto sm:bottom-0 sm:w-[230px] sm:translate-x-0 sm:rotate-[-7deg]">
                <OperatorDashboardMockup />
              </div>
              <div className="absolute -bottom-2 right-4 hidden rounded-2xl border border-[#d9c4a0] bg-[#f7f1e4]/95 px-4 py-3 shadow-lg sm:block">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[#8b6b3d]">Last reading</p>
                <p className="heading-display text-2xl text-[#1a2419]">152°F</p>
                <p className="text-xs text-[#617267]">Pad A — 12 · photo confirmed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#e4ddd0] bg-[#f3eee4] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="label-caps text-earth-600">The old way</p>
            <h2 className="heading-display mt-3 text-4xl text-[#1a2419] sm:text-5xl">
              Paper in the loader does not scale.
            </h2>
            <p className="mt-4 text-[#4a574e]">
              If the log lives on a clipboard, you only know what happened when someone is at the yard. That is a ceiling on growth.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                icon: ClipboardList,
                title: "The sheet gets lost",
                desc: "Wet paper, a missing pad, a row nobody filled in. When you need the history, it is already gone.",
              },
              {
                icon: AlertTriangle,
                title: "You cannot see the yard from town",
                desc: "Temperatures and turns stay in one person's notebook. The owner is flying blind.",
              },
              {
                icon: Timer,
                title: "Evenings become data entry",
                desc: "Someone retypes the day's work into a spreadsheet. That time should be on the piles, not the keyboard.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#e0d6c4] bg-[#faf7f1] p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#f0e2cc]">
                  <Icon className="text-[#8b6b3d]" size={22} />
                </div>
                <h3 className="heading-display text-2xl text-[#1a2419]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4a574e]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="label-caps text-earth-600">What ships today</p>
            <h2 className="heading-display mt-3 text-4xl text-[#1a2419] sm:text-5xl">
              Built for the person on the pad.
            </h2>
            <p className="mt-4 text-[#4a574e]">
              The iPhone is the product in the yard. The office sees the same piles, recipes, and history.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Camera,
                title: "Photo temperatures",
                desc: "Point the iPhone at the thermometer. The reading is suggested from the photo. You confirm it before it saves.",
                badge: "On the phone",
              },
              {
                icon: Layers,
                title: "Recipes on the pile",
                desc: "Save what went into the batch. Look it up later from the office, not from a stained notebook.",
                badge: "On the phone",
              },
              {
                icon: RotateCcw,
                title: "Turns & materials",
                desc: "Log a turn, water, and inbound feedstock on the row you are standing in front of.",
                badge: "On the phone",
              },
              {
                icon: MapPin,
                title: "Yard map",
                desc: "Draw the windrow on the pad. Find it again when the piles have moved.",
                badge: "On the phone",
              },
              {
                icon: LayoutDashboard,
                title: "Office view",
                desc: "See incoming and outgoing work, BOLs, and work orders from the desktop — Professional and up.",
                badge: "Office",
              },
              {
                icon: FileCheck,
                title: "History on the pile",
                desc: "Every temperature, turn, and material sits on that row, timestamped, with the photo attached. That is the audit trail.",
                badge: "On the phone",
              },
            ].map(({ icon: Icon, title, desc, badge }) => (
              <div
                key={title}
                className="rounded-2xl border border-[#e4ddd0] bg-[#faf7f1] p-6 transition-shadow hover:shadow-[0_18px_40px_-24px_rgba(26,36,25,0.45)]"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eaf0e9]">
                    <Icon className="text-[#2e5e32]" size={22} />
                  </div>
                  <span className="rounded-full bg-[#f0e2cc] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#8b6b3d]">
                    {badge}
                  </span>
                </div>
                <h3 className="heading-display text-2xl text-[#1a2419]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4a574e]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="overflow-hidden border-y border-[#e4ddd0] bg-[#f3eee4] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="label-caps text-earth-600">The field app</p>
            <h2 className="heading-display mt-3 text-4xl text-[#1a2419] sm:text-5xl">
              Rows, then record. That is the whole job.
            </h2>
            <p className="mt-4 text-[#4a574e]">
              Search the pad. Open the pile. Photograph the thermometer. No five-tab maze.
            </p>
          </div>

          <div className="mt-14 grid items-center gap-12 lg:grid-cols-2">
            <div className="flex justify-center">
              <div className="w-[240px]">
                <ActiveMockup />
              </div>
            </div>

            <div className="space-y-3">
              {SHOWCASE_SCREENS.map((screen, i) => (
                <button
                  key={screen.id}
                  onClick={() => setActiveScreen(i)}
                  className={`w-full rounded-2xl border p-4 text-left transition-all ${
                    activeScreen === i
                      ? "border-[#c9b48a] bg-[#faf7f1] shadow-[0_12px_28px_-18px_rgba(26,36,25,0.5)]"
                      : "border-transparent bg-transparent hover:bg-[#faf7f1]/70"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      activeScreen === i ? "bg-[#eaf0e9]" : "bg-[#ece6d8]"
                    }`}>
                      {screen.id === "dashboard" && <Layers size={16} className="text-[#2e5e32]" />}
                      {screen.id === "temp" && <Thermometer size={16} className="text-[#2e5e32]" />}
                      {screen.id === "turn" && <RotateCcw size={16} className="text-[#2e5e32]" />}
                      {screen.id === "material" && <Package size={16} className="text-[#2e5e32]" />}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${activeScreen === i ? "text-[#1a2419]" : "text-[#4a574e]"}`}>
                        {screen.label}
                      </p>
                      <p className="mt-0.5 text-xs text-[#617267]">
                        {screen.id === "dashboard" && "Find the row. See last temp. Tap to open it."}
                        {screen.id === "temp" && "Photo first. Confirm the number. It lives on the pile."}
                        {screen.id === "turn" && "Equipment, water, notes — logged where you turned."}
                        {screen.id === "material" && "What went on the pile, and how much."}
                      </p>
                    </div>
                    {activeScreen === i && (
                      <ChevronRight size={16} className="ml-auto shrink-0 text-[#2e5e32]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="compliance" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <p className="label-caps text-earth-600">The record</p>
              <h2 className="heading-display mt-3 text-4xl text-[#1a2419] sm:text-5xl">
                History lives on the pile.
              </h2>
              <p className="mt-5 max-w-lg leading-relaxed text-[#4a574e]">
                Process temperatures still matter. Compost Developer keeps the readings, photos, and turns on the row itself — so you can show what happened without digging through a binder.
              </p>

              <div className="mt-8 space-y-5">
                {[
                  {
                    title: "131°F is on the reading",
                    desc: "You see whether the number is above the thermophilic line when you log it. No mystery later.",
                  },
                  {
                    title: "Photo attached",
                    desc: "The thermometer picture sits with the temperature. That is the proof, not a typed cell in Excel.",
                  },
                  {
                    title: "Turns and recipes stay with the batch",
                    desc: "Open the pile weeks later and the mix, the turns, and the temps are still there.",
                  },
                  {
                    title: "The trail is on the pile",
                    desc: "Timestamped photos, turns, and recipes stay with the row. Formatted regulator PDFs are next — we will not sell a button that is not there yet.",
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="flex gap-4">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eaf0e9]">
                      <Check size={14} className="text-[#2e5e32]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#1a2419]">{title}</h4>
                      <p className="mt-0.5 text-sm text-[#4a574e]">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-[#e4ddd0] bg-[#faf7f1] p-8">
              <p className="label-caps text-earth-600">West yard · today</p>
              <h3 className="heading-display mt-2 text-3xl text-[#1a2419]">On the pads</h3>
              <div className="mt-6 space-y-3">
                {[
                  { name: "Pad A — 12", temp: "152°F", note: "Photo confirmed · 9:14a", ok: true },
                  { name: "Pad B — 07", temp: "148°F", note: "Turn #4 logged yesterday", ok: true },
                  { name: "Pad C — 03", temp: "128°F", note: "Below 131°F — check moisture", ok: false },
                ].map(({ name, temp, note, ok }) => (
                  <div
                    key={name}
                    className={`flex items-center justify-between rounded-2xl border p-4 ${
                      ok ? "border-[#d5e0d2] bg-[#f4f7f1]" : "border-[#ead9c0] bg-[#f8f1e4]"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-[#1a2419]">{name}</p>
                      <p className="mt-0.5 text-xs text-[#617267]">{note}</p>
                    </div>
                    <p className={`heading-display text-2xl ${ok ? "text-[#2e5e32]" : "text-[#8b6b3d]"}`}>{temp}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="border-y border-[#e4ddd0] bg-[#f3eee4] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="label-caps text-earth-600">Plans</p>
            <h2 className="heading-display mt-3 text-4xl text-[#1a2419] sm:text-5xl">
              Start on the phone. Grow into the office.
            </h2>
            <p className="mt-4 text-[#4a574e]">
              Every plan includes the iPhone field app. No annual contract unless you pay the year up front.
            </p>
          </div>

          <div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex flex-col rounded-3xl border border-[#e0d6c4] bg-[#faf7f1] p-8">
              <h3 className="heading-display text-3xl text-[#1a2419]">Starter</h3>
              <p className="mt-1 text-sm text-[#617267]">Tracking on the iPhone</p>
              <div className="mt-6 mb-1">
                <span className="heading-display text-5xl text-[#1a2419]">$299</span>
                <span className="text-sm text-[#617267]">/mo</span>
              </div>
              <p className="mb-6 text-xs text-[#617267]">$249/mo billed annually · 3 seats</p>
              <ul className="flex-1 space-y-3">
                {[
                  "iPhone field app",
                  "Photo temperatures",
                  "Turns, recipes, materials",
                  "Pile history",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4a574e]">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#2e5e32]" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/book-demo"
                className="mt-8 block rounded-full border border-[#c9b48a] px-6 py-3 text-center text-sm font-semibold text-[#1a2419] hover:bg-[#f0e2cc]"
              >
                Book a demo
              </Link>
            </div>

            <div className="relative flex flex-col rounded-3xl border-2 border-[#2e5e32] bg-[#faf7f1] p-8">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2e5e32] px-3 py-1 text-xs font-medium text-white">
                Most yards start here
              </div>
              <h3 className="heading-display text-3xl text-[#1a2419]">Professional</h3>
              <p className="mt-1 text-sm text-[#617267]">Tracking plus office operations</p>
              <div className="mt-6 mb-1">
                <span className="heading-display text-5xl text-[#1a2419]">$599</span>
                <span className="text-sm text-[#617267]">/mo</span>
              </div>
              <p className="mb-6 text-xs text-[#617267]">$499/mo billed annually · 5 seats</p>
              <ul className="flex-1 space-y-3">
                {[
                  "Everything in Starter",
                  "BOLs & work orders",
                  "Office operations",
                  "Pile history for audits",
                  "Priority support",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4a574e]">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#2e5e32]" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/book-demo"
                className="mt-8 block rounded-full bg-[#2e5e32] px-6 py-3 text-center text-sm font-semibold text-white hover:bg-[#264027]"
              >
                Book a demo
              </Link>
            </div>

            <div className="flex flex-col rounded-3xl border border-[#e0d6c4] bg-[#faf7f1] p-8">
              <h3 className="heading-display text-3xl text-[#1a2419]">Enterprise</h3>
              <p className="mt-1 text-sm text-[#617267]">Custom, multi-site, sales</p>
              <div className="mt-6 mb-1">
                <span className="heading-display text-5xl text-[#1a2419]">$1,499</span>
                <span className="text-sm text-[#617267]">/mo</span>
              </div>
              <p className="mb-6 text-xs text-[#617267]">Custom annual · 10+ seats</p>
              <ul className="flex-1 space-y-3">
                {[
                  "White-glove setup",
                  "Multi-site",
                  "Custom sales & marketing",
                  "Dedicated account manager",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4a574e]">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#2e5e32]" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/book-demo"
                className="mt-8 block rounded-full border border-[#c9b48a] px-6 py-3 text-center text-sm font-semibold text-[#1a2419] hover:bg-[#f0e2cc]"
              >
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="landing-grain relative overflow-hidden bg-[#1a2419] py-24">
        <div className="relative z-[2] mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="heading-display text-4xl text-[#f4efe4] sm:text-5xl">
              Come see it on a pile.
            </h2>
            <p className="mt-4 text-[#c5cfc4]">
              Thirty minutes. Your yard, your rows. We will show the iPhone and the office view — not a generic deck.
            </p>
          </div>
          <div className="rounded-3xl border border-[#3c5233] bg-[#faf7f1] p-8">
            <DemoForm compact />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
